import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { useMentionUiStore } from '../../../../stores/mentionUiStore';
import type { GenerationAsset } from '../../state/types';
import {
  buildMentionCandidates,
  buildMentionToken,
  mentionBadgeLabel,
  type MentionActorSource,
  type MentionCandidate,
} from '../../state/mentionUtils';
import type { SceneFrameMentionSourceRow } from '../utils';

// 1回のメンション入力として許容する最大クエリ長。これを超えたら「@」からの入力を
// メンションとみなさない (空白なしの長文で毎キー入力ごとバックスキャンし続ける
// パフォーマンス劣化を防ぐ安全弁)。
const MAX_MENTION_QUERY_LENGTH = 40;

// actors 省略時の既定値。呼び出しごとに新しい配列リテラルを渡すと useMemo の依存が
// 毎回変わり allCandidates が再計算され続けるため、安定した参照を1つ共有する。
const EMPTY_ACTORS: MentionActorSource[] = [];
const EMPTY_CANDIDATES: MentionCandidate[] = [];
const EMPTY_SCENE_SOURCES: SceneFrameMentionSourceRow[] = [];

export type SceneFramePosition = 'first' | 'last';

/** 「他シーンの動画」行のフレーム確保結果 (呼び出し側が挿入APIを呼んで返す)。 */
export type SceneFrameInsertResult = { frameMaterialId: string; id4: string };

type ActiveMention = { start: number; end: number; query: string };

export type MentionPopupPosition = { top: number; left: number };

/**
 * textarea と同じフォント・パディング・ボーダー・幅を持つ非表示 div (「ミラー要素」) を
 * body へ追加して返す。textarea 自体はキャレット座標や文字単位の矩形を取得する DOM API を
 * 持たないため、同じレイアウトで折り返す複製要素にテキストを流し込んで計測する手法
 * (getCaretViewportPosition・useMentionTokenChips のトークン矩形計測が共通で使う)。
 * 呼び出し側が計測後に document.body.removeChild で取り除くこと。
 */
export function createTextareaMirror(textarea: HTMLTextAreaElement): HTMLDivElement {
  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const propsToCopy: (keyof CSSStyleDeclaration)[] = [
    'boxSizing', 'width', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderStyle',
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight',
    'textTransform', 'textIndent', 'textAlign', 'tabSize',
  ];
  propsToCopy.forEach((prop) => {
    // computed style の値をそのままミラーへコピー (フォント計測を一致させるため)
    (mirror.style as unknown as Record<string, string>)[prop as string] = style[prop] as string;
  });
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflow = 'hidden';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';
  mirror.style.height = 'auto';
  document.body.appendChild(mirror);
  return mirror;
}

/**
 * textarea 内の index 文字目 (キャレット位置) のビューポート座標を計算する。
 * 「ミラー要素方式」(createTextareaMirror): テキストを流し込み、対象位置に span を
 * 挿入してその位置を測る。
 */
function getCaretViewportPosition(textarea: HTMLTextAreaElement, index: number): MentionPopupPosition {
  const style = window.getComputedStyle(textarea);
  const mirror = createTextareaMirror(textarea);

  const before = textarea.value.slice(0, index);
  mirror.textContent = before;
  const marker = document.createElement('span');
  // 空文字だと offsetTop/offsetLeft が測れないブラウザがあるため 1 文字分のダミーを入れる
  marker.textContent = '​';
  mirror.appendChild(marker);

  const markerTop = marker.offsetTop;
  const markerLeft = marker.offsetLeft;
  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;

  document.body.removeChild(mirror);

  const rect = textarea.getBoundingClientRect();
  return {
    top: rect.top - textarea.scrollTop + markerTop + lineHeight,
    left: rect.left - textarea.scrollLeft + markerLeft,
  };
}

/**
 * cursor 位置から後方走査し、現在入力中の @メンションクエリを検出する。
 * 直前が ASCII 英数字の「@」(メールアドレス・SNS ハンドル等の途中) は対象外。
 * 日本語の文章は空白で区切られない (「…カメラ固定。@」のように句読点・漢字の直後に
 * 打つのが通常) ため、空白/行頭だけを許可すると日本語文中で永久に発火しない。
 * @ とカーソルの間に空白があれば「既に閉じたメンション」とみなし対象外にする。
 */
function detectActiveMention(text: string, cursor: number): { start: number; query: string } | null {
  let i = cursor - 1;
  let scanned = 0;
  while (i >= 0 && scanned < MAX_MENTION_QUERY_LENGTH) {
    const ch = text[i];
    if (ch === '@') {
      const prev = i > 0 ? text[i - 1] : null;
      if (prev !== null && /[A-Za-z0-9]/.test(prev)) return null;
      return { start: i, query: text.slice(i + 1, cursor) };
    }
    if (/\s/.test(ch)) return null;
    i -= 1;
    scanned += 1;
  }
  return null;
}

function filterSceneSources(sources: SceneFrameMentionSourceRow[], query: string): SceneFrameMentionSourceRow[] {
  if (!query) return sources;
  const q = query.toLowerCase();
  return sources.filter((row) => row.label.toLowerCase().includes(q) || String(row.order) === q);
}

function filterCandidates(candidates: MentionCandidate[], query: string): MentionCandidate[] {
  if (!query) return candidates;
  const q = query.toLowerCase();
  return candidates.filter((c) => {
    if (c.badge.toLowerCase().startsWith(q)) return true;
    const name = c.target.name?.toLowerCase() ?? '';
    return name.includes(q);
  });
}

interface UseMaterialMentionParams {
  /** ポップアップの接続先 textarea。挿入後の selectionStart 復元にも使う。 */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
  /** selectedAssets 等、候補の母集団。isMentionableReferenceAsset を満たすもののみ候補化される。 */
  assets: GenerationAsset[];
  /** AI俳優 (casting/seedance-preset) の候補。常に候補になる (参照上限フィルタ対象外)。
   *  俳優 → 素材の順で 1 本のバッジシーケンスにする
   *  (docs/features/material-at-mention-plan.md「俳優メンション」節)。省略時は俳優なし。 */
  actors?: MentionActorSource[];
  /** ポップアップ (portal 先要素) への ref。外側クリック判定に使う。 */
  popupRef: React.RefObject<HTMLElement | null>;
  /**
   * 記録 (generation_metadata.scene_frame_mentions)から再構成したシーンフレーム候補
   * (buildSceneFrameMentionCandidates)。ポップアップの行には出さず、バッジの払い出し
   * (俳優→素材→シーンフレーム)と挿入時の衝突警告にだけ使う
   * (docs/features/scene-video-frame-materials-visibility-plan.md §2.2)。
   */
  sceneFrameCandidates?: MentionCandidate[];
  /** ポップアップの「他シーンの動画」節の行 (listSceneFrameMentionSources)。 */
  sceneFrameSources?: SceneFrameMentionSourceRow[];
  /**
   * 行の[最初] [最後]で呼ばれる。フレームを確保して記録を更新し、frame_material_idとid4を返す。
   * 失敗時は呼び出し側でトーストしnullを返す (挿入しない)。
   */
  onInsertSceneFrame?: (row: SceneFrameMentionSourceRow, position: SceneFramePosition) => Promise<SceneFrameInsertResult | null>;
  /**
   * 編集セッションの識別子 (編集開始ごとに変わる値・編集していない間はnull)。
   * [最初]/[最後]の確保待ちの間にキャンセル・保存・別シーン/別欄への切り替えで編集が
   * 変わったら、完了時にトークンを挿入しない (共有のeditingValueが別の入力欄の本文に
   * なっているため)。省略時はtextarea要素の同一性だけで判定する。
   */
  editSessionKey?: string | number | null;
}

/** 非同期挿入を始めた時点の編集セッション (完了時に同じかを確かめる)。 */
type SceneFrameInsertSession = {
  key: string | number | null | undefined;
  textarea: HTMLTextAreaElement | null;
};

export const SCENE_FRAME_INSERT_ABANDONED_MESSAGE = '編集を終えたため、トークンは挿入されませんでした';

export interface UseMaterialMentionResult {
  isOpen: boolean;
  candidates: MentionCandidate[];
  /** 「他シーンの動画」節の行 (candidatesの後ろに続く。activeIndexはcandidates.lengthから数える)。 */
  sceneFrameRows: SceneFrameMentionSourceRow[];
  /** キーボード操作で選ばれている[最初]/[最後] (アクティブ行にだけ効く。既定[最後])。 */
  sceneFramePosition: SceneFramePosition;
  /** フレーム確保中の行とボタン (押下中はスピナー/非活性)。 */
  pendingSceneFrame: { sceneId: string; position: SceneFramePosition } | null;
  selectSceneFrame: (row: SceneFrameMentionSourceRow, position: SceneFramePosition) => void;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  popupPosition: MentionPopupPosition | null;
  close: () => void;
  selectCandidate: (candidate: MentionCandidate) => void;
  textareaHandlers: {
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onSelect: (event: React.SyntheticEvent<HTMLTextAreaElement>) => void;
    onCompositionStart: () => void;
    onCompositionEnd: (event: React.CompositionEvent<HTMLTextAreaElement>) => void;
  };
}

/**
 * 動画プロンプト欄・シーン説明欄 (P3) 共通の @メンションオートコンプリートフック。
 * 素の textarea を維持したまま「@」入力でカーソル位置付近にポップアップを出し、
 * 選択で `@<バッジ文字>#<4hex>` トークンを挿入する
 * (docs/features/material-at-mention-plan.md 案C)。
 */
export function useMaterialMention({
  textareaRef,
  value,
  onChange,
  assets,
  actors = EMPTY_ACTORS,
  popupRef,
  sceneFrameCandidates = EMPTY_CANDIDATES,
  sceneFrameSources = EMPTY_SCENE_SOURCES,
  onInsertSceneFrame,
  editSessionKey,
}: UseMaterialMentionParams): UseMaterialMentionResult {
  const toast = useToast();
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [popupPosition, setPopupPosition] = useState<MentionPopupPosition | null>(null);
  const isComposingRef = useRef(false);

  const allCandidates = useMemo(
    () => buildMentionCandidates(assets, actors, sceneFrameCandidates),
    [assets, actors, sceneFrameCandidates],
  );
  // 記録由来のシーンフレームは行に出さない(新規挿入は「他シーンの動画」節のボタンから。
  // 再挿入もAPIを通して記録のtouched_atを更新させるため)。
  const popupCandidates = useMemo(
    () => allCandidates.filter((c) => c.kind !== 'scene_frame'),
    [allCandidates],
  );
  const sceneSourcesEnabled = !!onInsertSceneFrame;
  const candidates = useMemo(
    () => (activeMention ? filterCandidates(popupCandidates, activeMention.query) : []),
    [popupCandidates, activeMention],
  );
  const sceneFrameRows = useMemo(
    () => (activeMention && sceneSourcesEnabled ? filterSceneSources(sceneFrameSources, activeMention.query) : []),
    [sceneFrameSources, activeMention, sceneSourcesEnabled],
  );
  const totalRows = candidates.length + sceneFrameRows.length;
  // 候補ゼロなら出さない (activeMention があってもフィルタ後 0 件なら閉じる)
  const isOpen = activeMention !== null && totalRows > 0;
  const hasAnySource = popupCandidates.length > 0 || (sceneSourcesEnabled && sceneFrameSources.length > 0);

  const [sceneFramePosition, setSceneFramePosition] = useState<SceneFramePosition>('last');
  const [pendingSceneFrame, setPendingSceneFrame] = useState<{ sceneId: string; position: SceneFramePosition } | null>(null);
  const pendingSceneFrameRef = useRef(false);
  // 非同期挿入の完了時に最新の本文へ差し込むため、valueをrefでも持つ
  const valueRef = useRef(value);
  valueRef.current = value;
  // 確保完了時に編集セッションが続いているかを確かめるため、最新の識別子をrefで持つ
  const editSessionKeyRef = useRef(editSessionKey);
  editSessionKeyRef.current = editSessionKey;
  // アンマウント後の完了ではsetStateしない
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ポップアップの開閉を共有ストアへ通知する。SelectedAssetsPanel はこれを購読し、
  // 「@入力中だけ」参照バッジ (A, B, C…) を表示する (常時表示はノイズになるため)。
  const incrementOpenPopup = useMentionUiStore((s) => s.incrementOpenPopup);
  const decrementOpenPopup = useMentionUiStore((s) => s.decrementOpenPopup);
  useEffect(() => {
    if (!isOpen) return;
    incrementOpenPopup();
    return () => decrementOpenPopup();
  }, [isOpen, incrementOpenPopup, decrementOpenPopup]);

  const close = useCallback(() => {
    setActiveMention(null);
  }, []);

  // activeIndex は候補件数の変化に追従してクランプする
  useEffect(() => {
    if (activeIndex >= totalRows) {
      setActiveIndex(Math.max(0, totalRows - 1));
    }
  }, [totalRows, activeIndex]);

  // アクティブ行が変わったら[最初]/[最後]の選択は既定の[最後]に戻す
  useEffect(() => {
    setSceneFramePosition('last');
  }, [activeIndex]);

  // ポップアップ表示中、キャレット位置座標を計算する (textarea のミラー要素方式)
  useEffect(() => {
    if (!isOpen || !activeMention) {
      setPopupPosition(null);
      return;
    }
    const ta = textareaRef.current;
    if (!ta) return;
    const recompute = () => setPopupPosition(getCaretViewportPosition(ta, activeMention.start));
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [isOpen, activeMention, textareaRef]);

  // モーダル内でも使えるよう、textarea・ポップアップの外側クリックで閉じる
  // (usePortalDropdown と同じ mousedown ベースの外側クリック判定様式)
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (popupRef.current?.contains(target)) return;
      if (textareaRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, popupRef, textareaRef, close]);

  const runDetection = useCallback((text: string, cursor: number) => {
    if (isComposingRef.current) return;
    if (!hasAnySource) {
      setActiveMention(null);
      return;
    }
    const detected = detectActiveMention(text, cursor);
    if (detected) {
      setActiveMention((prev) => {
        const end = cursor;
        if (prev && prev.start === detected.start && prev.query === detected.query && prev.end === end) return prev;
        return { start: detected.start, end, query: detected.query };
      });
      setActiveIndex(0);
    } else {
      setActiveMention(null);
    }
  }, [hasAnySource]);

  const selectCandidate = useCallback((candidate: MentionCandidate) => {
    const mention = activeMention;
    if (!mention) return;
    const token = buildMentionToken(candidate.badge, candidate.target.id);
    const nextValue = value.slice(0, mention.start) + token + ' ' + value.slice(mention.end);
    const nextCursor = mention.start + token.length + 1;
    onChange(nextValue);
    setActiveMention(null);

    // 同時衝突ガード: 挿入した対象 (素材/俳優) の 4hex が他の候補とも一致する場合、
    // 生成時にこの参照が解決されないため挿入は許可しつつ 1 回警告する。
    const collidesWithOther = allCandidates.some(
      (c) => c.target.id !== candidate.target.id && c.idHex4 === candidate.idHex4,
    );
    if (collidesWithOther) {
      toast.show('同じ短縮IDの素材/俳優が複数選択されています。生成時にこの参照は解決されません', 8000);
    }

    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(nextCursor, nextCursor);
    });
  }, [activeMention, value, onChange, allCandidates, toast, textareaRef]);

  const selectSceneFrame = useCallback((row: SceneFrameMentionSourceRow, position: SceneFramePosition) => {
    const mention = activeMention;
    if (!mention || !onInsertSceneFrame || pendingSceneFrameRef.current) return;
    const originalSlice = valueRef.current.slice(mention.start, mention.end);
    const session: SceneFrameInsertSession = { key: editSessionKeyRef.current, textarea: textareaRef.current };
    pendingSceneFrameRef.current = true;
    setPendingSceneFrame({ sceneId: row.sceneId, position });
    void (async () => {
      let result: SceneFrameInsertResult | null = null;
      try {
        result = await onInsertSceneFrame(row, position);
      } finally {
        pendingSceneFrameRef.current = false;
        if (mountedRef.current) setPendingSceneFrame(null);
      }
      if (!result) return;
      // 押下時と同じ編集が続いているか (フックがマウント中・同じtextareaが表示中・
      // 同じ編集セッション)。キャンセル・保存・別シーン/別欄への切り替え後は挿入しない
      const currentTextarea = textareaRef.current;
      const sameSession = mountedRef.current
        && !!session.textarea
        && currentTextarea === session.textarea
        && currentTextarea.isConnected
        && editSessionKeyRef.current === session.key;
      if (!sameSession) {
        if (mountedRef.current) setActiveMention(null);
        toast.show(SCENE_FRAME_INSERT_ABANDONED_MESSAGE, 4000);
        return;
      }
      // 既に記録にあるフレーム (再挿入)はそのバッジ、新規は全候補の末尾のバッジ
      // (記録の再構成も挿入時刻順で末尾に付くので揃う)。
      const existing = allCandidates.find((c) => c.target.id === result!.frameMaterialId);
      const badge = existing ? existing.badge : mentionBadgeLabel(allCandidates.length);
      const token = `@${badge}#${result.id4}`;
      const current = valueRef.current;
      // 確保中に本文が変わって「@クエリ」の位置がずれた場合は、カーソル位置へ差し込む
      const ta = currentTextarea;
      const [start, end] = current.slice(mention.start, mention.end) === originalSlice
        ? [mention.start, mention.end]
        : [ta?.selectionStart ?? current.length, ta?.selectionEnd ?? current.length];
      const nextValue = current.slice(0, start) + token + ' ' + current.slice(end);
      const nextCursor = start + token.length + 1;
      onChange(nextValue);
      setActiveMention(null);

      const collidesWithOther = allCandidates.some(
        (c) => c.target.id !== result!.frameMaterialId && c.idHex4 === result!.id4,
      );
      if (collidesWithOther) {
        toast.show('同じ短縮IDの素材/俳優が複数あります。生成時にこの参照は解決されません', 8000);
      }

      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      });
    })();
  }, [activeMention, onInsertSceneFrame, allCandidates, onChange, toast, textareaRef]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value;
    const cursor = event.target.selectionStart;
    onChange(next);
    runDetection(next, cursor);
  }, [onChange, runDetection]);

  const handleSelect = useCallback((event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const ta = event.currentTarget;
    runDetection(ta.value, ta.selectionStart);
  }, [runDetection]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // IME 変換中 (isComposing / keyCode 229) はメンションのキー操作を一切奪わない
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (!isOpen) return;

    // ポップアップ表示中は、ホスト側の既存キーハンドラ (SceneEditModal の
    // 「Enter で保存」等) より優先してイベントを止める。
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      setActiveIndex((idx) => (totalRows === 0 ? 0 : (idx + 1) % totalRows));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      setActiveIndex((idx) => (totalRows === 0 ? 0 : (idx - 1 + totalRows) % totalRows));
      return;
    }
    const activeSceneRow = activeIndex >= candidates.length ? sceneFrameRows[activeIndex - candidates.length] : undefined;
    // 「他シーンの動画」行では ←→ で[最初]/[最後]を切り替える (それ以外の行ではキャレット移動のまま)
    if (activeSceneRow && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      event.stopPropagation();
      setSceneFramePosition(event.key === 'ArrowLeft' ? 'first' : 'last');
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      if (activeSceneRow) {
        event.preventDefault();
        event.stopPropagation();
        selectSceneFrame(activeSceneRow, sceneFramePosition);
        return;
      }
      const chosen = candidates[activeIndex];
      if (chosen) {
        event.preventDefault();
        event.stopPropagation();
        selectCandidate(chosen);
      }
      return;
    }
  }, [isOpen, candidates, sceneFrameRows, totalRows, activeIndex, close, selectCandidate, selectSceneFrame, sceneFramePosition]);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback((event: React.CompositionEvent<HTMLTextAreaElement>) => {
    isComposingRef.current = false;
    const ta = event.currentTarget;
    runDetection(ta.value, ta.selectionStart);
  }, [runDetection]);

  return {
    isOpen,
    candidates,
    sceneFrameRows,
    sceneFramePosition,
    pendingSceneFrame,
    selectSceneFrame,
    activeIndex,
    setActiveIndex,
    popupPosition,
    close,
    selectCandidate,
    textareaHandlers: {
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onSelect: handleSelect,
      onCompositionStart: handleCompositionStart,
      onCompositionEnd: handleCompositionEnd,
    },
  };
}
